import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Clock, Award } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function QuizList() {
  const [student, setStudent] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStudentProfile();
    fetchQuizzes();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('studentToken');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchStudentProfile = async () => {
    try {
      const response = await axios.get(`${API}/students/profile`, getAuthHeaders());
      setStudent(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await axios.get(`${API}/quizzes`);
      setQuizzes(response.data);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterQuizzesByBranch = () => {
    if (!student) return quizzes;
    return quizzes.filter(quiz => quiz.branch === student.branch);
  };

  const filteredQuizzes = filterQuizzesByBranch();

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm">
      {student && (
        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg border border-blue-100">
          <h2 className="text-xl font-bold text-brand-dark mb-1">Welcome, {student.name}!</h2>
          <p className="text-slate-600">Branch: <span className="font-medium text-brand-blue">{student.branch}</span></p>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-3xl font-bold text-brand-dark font-display">Available Quizzes</h1>
        <p className="text-slate-600 mt-2">Practice quizzes tailored for your branch</p>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-blue border-r-transparent"></div>
          <p className="mt-4 text-slate-600">Loading quizzes...</p>
        </div>
      ) : filteredQuizzes.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">No quizzes available for your branch yet.</p>
          <p className="text-slate-500 text-sm mt-2">Check back soon!</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredQuizzes.map((quiz) => (
            <div
              key={quiz.quiz_id}
              data-testid={`quiz-card-${quiz.quiz_id}`}
              className="border border-slate-200 rounded-lg p-6 hover:shadow-md transition-all hover:border-brand-blue"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-brand-dark mb-2">{quiz.title}</h3>
                  <p className="text-brand-blue font-medium text-sm">{quiz.subject}</p>
                </div>
                <BookOpen className="h-8 w-8 text-brand-blue" />
              </div>

              <div className="flex items-center gap-4 text-sm text-slate-600 mb-4">
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-1" />
                  <span>{quiz.questions.length} Questions</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-4 w-4 mr-1" />
                  <span>15 mins</span>
                </div>
              </div>

              <Link to={`/student/quiz/${quiz.quiz_id}`}>
                <Button data-testid={`start-quiz-${quiz.quiz_id}`} className="w-full bg-brand-blue hover:bg-blue-600">
                  Start Quiz
                </Button>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
